// Libraries
import React, {forwardRef} from 'react'
import classnames from 'classnames'

// Types
import {StandardFunctionProps, InfluxColors} from '../../Types'

// Styles
import './InfluxDataLogo.scss'

export interface InfluxDataLogoProps extends StandardFunctionProps {
  /** Coloration of the SVG image */
  fill?: InfluxColors | string
  /** Remove 'Act in time' from the logo */
  simplified?: boolean
}

export type InfluxDataLogoRef = SVGSVGElement

export const InfluxDataLogo = forwardRef<
  InfluxDataLogoRef,
  InfluxDataLogoProps
>(
  (
    {
      id,
      style,
      fill = InfluxColors.White,
      testID = 'logo--influxdata',
      className,
      simplified = false,
    },
    ref
  ) => {
    const logoClass = classnames('cf-logo--influxdata', {
      [`${className}`]: className,
    })

    const logoStyle = {fill, ...style}

    if (simplified) {
      return (
        <svg
          version="1.1"
          x="0px"
          y="0px"
          width="44"
          height="45"
          fill="none"
          viewBox="0 0 44 45"
          style={logoStyle}
          ref={ref}
          id={id}
          data-testid={testID}
        >
          <path
            d="M32.2,31.3L42.4,29c0.2,0,0.3-0.1,0.4-0.2c0.1-0.1,0.2-0.2,0.3-0.3c0.1-0.1,0.1-0.3,0.2-0.5c0-0.2,0-0.3,0-0.5
          L38.9,8.7c-0.1-0.3-0.3-0.6-0.6-0.7c-0.3-0.2-0.6-0.2-0.9-0.1l-10.2,2.3c-0.3,0.1-0.6,0.3-0.7,0.6c-0.2,0.3-0.2,0.6-0.1,0.9
          l4.3,18.8c0.1,0.3,0.3,0.6,0.6,0.7C31.6,31.4,31.9,31.4,32.2,31.3z"
          />
          <path
            d="M28.7,43l12.4-11.5c0.5-0.5,0.3-0.8-0.3-0.5l-8.5,1.9c-0.4,0.1-0.7,0.3-0.9,0.5c-0.3,0.2-0.5,0.6-0.6,0.9
          l-2.6,8.3C28,43.3,28.2,43.5,28.7,43z"
          />
          <path
            d="M6.1,39.2L24.6,45c0.3,0.1,0.6,0,0.9-0.2s0.5-0.4,0.6-0.7l3.1-10c0-0.2,0.1-0.3,0-0.5c0-0.2-0.1-0.3-0.1-0.5
          c-0.1-0.1-0.2-0.3-0.3-0.4c-0.1-0.1-0.3-0.2-0.4-0.2L9.9,26.9C9.6,26.8,9.2,26.8,9,27c-0.3,0.2-0.5,0.4-0.6,0.7l-3.1,9.9
          c-0.1,0.3-0.1,0.6,0.1,0.9C5.5,38.9,5.8,39.1,6.1,39.2z"
          />
          <path
            d="M0.1,18.9l3.8,16.5C4,36,4.3,36,4.4,35.3L7,27c0.1-0.4,0.1-0.7,0-1.1s-0.2-0.7-0.5-1l-5.9-6.4
          C0.2,18.1-0.1,18.2,0.1,18.9z"
          />
          <path
            d="M14.6,1.3L0.4,14.4C0.1,14.6,0,14.9,0,15.3c0,0.3,0.1,0.6,0.3,0.9l7.1,7.7c0.2,0.2,0.5,0.4,0.8,0.4
          c0.3,0,0.6-0.1,0.9-0.3l14.2-13.2c0.2-0.2,0.4-0.5,0.4-0.8s-0.1-0.6-0.3-0.9l-7.1-7.7c-0.1-0.1-0.2-0.2-0.4-0.3
          C15.8,1,15.6,1,15.4,1s-0.3,0-0.5,0.1C14.8,1.1,14.7,1.2,14.6,1.3z"
          />
          <path
            d="M28.3,31c0.6,0.2,1.1-0.2,0.9-0.8l-4.1-17.8c-0.2-0.6-0.7-0.8-1.2-0.4L10.5,24.5c-0.5,0.5-0.4,1,0.3,1.2
          L28.3,31z"
          />
          <path
            d="M35.3,6.2l-16.2-5c-0.6-0.2-0.8,0.1-0.2,0.6l5.9,6.4c0.3,0.2,0.6,0.4,0.9,0.5c0.4,0.1,0.7,0.1,1.1,0l8.5-1.9
          C36,6.7,36,6.4,35.3,6.2z"
          />
        </svg>
      )
    }

    return (
      <svg
        version="1.1"
        ref={ref}
        id={id}
        data-testid={testID}
        style={logoStyle}
        x="0px"
        y="0px"
        viewBox="0 0 270 45"
        width="270"
        height="45"
      >
        <path
          className={logoClass}
          d="M64.9,12.8c-1.1,0-2-0.4-2.8-1.1C61.4,11,61,10.2,61,9.1s0.4-1.9,1.1-2.6c0.8-0.7,1.7-1.1,2.8-1.1
        c1.1,0,2,0.4,2.7,1.1s1.1,1.6,1.1,2.6s-0.4,1.9-1.1,2.6C66.8,12.4,65.9,12.8,64.9,12.8z M61.4,35.4V15.9h6.8v19.6L61.4,35.4
        L61.4,35.4z"
        />
        <path
          className={logoClass}
          d="M72.9,35.4V15.9h6.7l0.1,2.4c0.5-1,1.2-1.7,2.2-2.2s2.1-0.8,3.4-0.8c2.2,0,3.9,0.7,5,2.1
        c1.2,1.4,1.7,3.4,1.7,6.1v11.9h-6.8V24.8c0-2.3-0.9-3.5-2.6-3.5c-0.9,0-1.7,0.3-2.2,1c-0.5,0.6-0.7,1.5-0.7,2.7v10.4H72.9z"
        />
        <path
          className={logoClass}
          d="M98.5,35.4v-14h-3.1v-5.5h3.1V14c0-3,0.7-5.3,2.2-7c1.5-1.6,3.6-2.5,6.3-2.5c0.8,0,1.5,0.1,2.2,0.2
        s1.3,0.3,1.8,0.5l-0.3,5.7c-0.3-0.1-0.6-0.2-1-0.3s-0.7-0.1-1.1-0.1c-1.1,0-1.9,0.3-2.4,0.9c-0.5,0.6-0.8,1.6-0.8,3v1.4h4.6v5.5
        h-4.6v14.1H98.5z M113,35.4V4.7h6.8v30.7H113z"
        />
        <path
          className={logoClass}
          d="M130.9,36c-2.1,0-3.8-0.7-4.9-2c-1.1-1.4-1.7-3.4-1.7-6.1v-12h6.9v10.7c0,1.2,0.2,2,0.6,2.6
        c0.4,0.6,1.1,0.8,2,0.8s1.6-0.3,2.1-0.9c0.5-0.7,0.8-1.6,0.8-2.8V15.9h6.8v19.6h-6.7l-0.1-2.4c-0.5,1-1.3,1.7-2.3,2.2
        C133.4,35.8,132.2,36,130.9,36z"
        />
        <path
          className={logoClass}
          d="M146.6,35.4l7.1-10.1l-6.7-9.5h7.6l3.2,5.3l3.1-5.3h7.3l-6.7,9.6l7.1,10h-7.7l-3.6-5.9l-3.5,5.9H146.6z"
        />
        <path
          className={logoClass}
          d="M180.6,36c-1.7,0-3.3-0.4-4.7-1.3c-1.4-0.9-2.4-2-3.2-3.6c-0.8-1.5-1.2-3.3-1.2-5.2s0.4-3.6,1.2-5.1
        c0.9-1.5,2-2.8,3.4-3.6c1.4-0.9,3-1.4,4.8-1.4c1.4,0,2.7,0.4,3.9,1.1s2.2,1.7,2.9,3V4.7h3.2v30.7h-3.1l-0.1-3.5
        c-0.8,1.3-1.8,2.3-3,3C183.6,35.7,182.1,36,180.6,36z M181.4,33.2c1.3,0,2.5-0.3,3.4-0.9c1-0.6,1.8-1.5,2.3-2.5
        c0.6-1.1,0.9-2.3,0.9-3.8c0-1.4-0.3-2.7-0.9-3.8c-0.6-1.1-1.3-1.9-2.3-2.5s-2.1-0.9-3.4-0.9c-1.3,0-2.4,0.3-3.4,0.9
        s-1.8,1.5-2.3,2.5c-0.6,1.1-0.8,2.3-0.8,3.8c0,1.4,0.3,2.7,0.8,3.8c0.6,1.1,1.4,1.9,2.3,2.5C179,32.9,180.1,33.2,181.4,33.2z"
        />
        <path
          className={logoClass}
          d="M205.6,36c-1.7,0-3.3-0.4-4.7-1.3c-1.4-0.9-2.4-2-3.2-3.6c-0.8-1.5-1.2-3.3-1.2-5.2s0.4-3.6,1.2-5.1
        c0.9-1.5,2-2.8,3.4-3.6c1.4-0.9,3-1.4,4.8-1.4c1.4,0,2.7,0.4,3.9,1.1s2.2,1.7,2.9,3l0.1-3.5h3.1v19h-3.1l-0.1-3.5
        c-0.8,1.3-1.8,2.3-3,3C208.6,35.7,207.2,36,205.6,36z M206.4,33.2c1.3,0,2.5-0.3,3.4-0.9c1-0.6,1.8-1.5,2.3-2.5
        c0.6-1.1,0.9-2.3,0.9-3.8c0-1.4-0.3-2.7-0.9-3.8c-0.6-1.1-1.3-1.9-2.3-2.5s-2.1-0.9-3.4-0.9c-1.3,0-2.4,0.3-3.4,0.9
        s-1.8,1.5-2.3,2.5c-0.6,1.1-0.8,2.3-0.8,3.8c0,1.4,0.3,2.7,0.8,3.8c0.6,1.1,1.4,1.9,2.3,2.5C204,32.9,205.1,33.2,206.4,33.2z"
        />
        <path
          className={logoClass}
          d="M230.3,36c-2.1,0-3.7-0.6-4.7-1.7c-1-1.2-1.5-3-1.5-5.4v-9.7h-4.3v-2.8h4.3V11h3.2v5.5h6.6v2.8h-6.6v9.4
        c0,1.6,0.3,2.8,0.8,3.5c0.6,0.7,1.5,1.1,2.8,1.1c1.2,0,2.5-0.3,3.7-1L235,35c-0.6,0.4-1.3,0.6-2.1,0.8C232.1,35.9,231.3,36,230.3,36
        z"
        />
        <path
          className={logoClass}
          d="M247,36c-1.7,0-3.3-0.4-4.7-1.3c-1.4-0.9-2.4-2-3.2-3.6c-0.8-1.5-1.2-3.3-1.2-5.2s0.4-3.6,1.2-5.1
        c0.9-1.5,2-2.8,3.4-3.6c1.4-0.9,3-1.4,4.8-1.4c1.4,0,2.7,0.4,3.9,1.1s2.2,1.7,2.9,3l0.1-3.5h3.1v19h-3.1l-0.1-3.5
        c-0.8,1.3-1.8,2.3-3,3C250,35.7,248.6,36,247,36z M247.8,33.2c1.3,0,2.5-0.3,3.4-0.9c1-0.6,1.8-1.5,2.3-2.5c0.6-1.1,0.9-2.3,0.9-3.8
        c0-1.4-0.3-2.7-0.9-3.8c-0.6-1.1-1.3-1.9-2.3-2.5s-2.1-0.9-3.4-0.9c-1.3,0-2.4,0.3-3.4,0.9s-1.8,1.5-2.3,2.5
        c-0.6,1.1-0.8,2.3-0.8,3.8c0,1.4,0.3,2.7,0.8,3.8c0.6,1.1,1.4,1.9,2.3,2.5C245.4,32.9,246.5,33.2,247.8,33.2z"
        />
        <path
          className={logoClass}
          d="M32.2,31.3L42.4,29c0.2,0,0.3-0.1,0.4-0.2c0.1-0.1,0.2-0.2,0.3-0.3c0.1-0.1,0.1-0.3,0.2-0.5c0-0.2,0-0.3,0-0.5
        L38.9,8.7c-0.1-0.3-0.3-0.6-0.6-0.7c-0.3-0.2-0.6-0.2-0.9-0.1l-10.2,2.3c-0.3,0.1-0.6,0.3-0.7,0.6c-0.2,0.3-0.2,0.6-0.1,0.9
        l4.3,18.8c0.1,0.3,0.3,0.6,0.6,0.7C31.6,31.4,31.9,31.4,32.2,31.3z"
        />
        <path
          className={logoClass}
          d="M28.7,43l12.4-11.5c0.5-0.5,0.3-0.8-0.3-0.5l-8.5,1.9c-0.4,0.1-0.7,0.3-0.9,0.5c-0.3,0.2-0.5,0.6-0.6,0.9
        l-2.6,8.3C28,43.3,28.2,43.5,28.7,43z"
        />
        <path
          className={logoClass}
          d="M6.1,39.2L24.6,45c0.3,0.1,0.6,0,0.9-0.2s0.5-0.4,0.6-0.7l3.1-10c0-0.2,0.1-0.3,0-0.5c0-0.2-0.1-0.3-0.1-0.5
        c-0.1-0.1-0.2-0.3-0.3-0.4c-0.1-0.1-0.3-0.2-0.4-0.2L9.9,26.9C9.6,26.8,9.2,26.8,9,27c-0.3,0.2-0.5,0.4-0.6,0.7l-3.1,9.9
        c-0.1,0.3-0.1,0.6,0.1,0.9C5.5,38.9,5.8,39.1,6.1,39.2z"
        />
        <path
          className={logoClass}
          d="M0.1,18.9l3.8,16.5C4,36,4.3,36,4.4,35.3L7,27c0.1-0.4,0.1-0.7,0-1.1s-0.2-0.7-0.5-1l-5.9-6.4
        C0.2,18.1-0.1,18.2,0.1,18.9z"
        />
        <path
          className={logoClass}
          d="M14.6,1.3L0.4,14.4C0.1,14.6,0,14.9,0,15.3c0,0.3,0.1,0.6,0.3,0.9l7.1,7.7c0.2,0.2,0.5,0.4,0.8,0.4
        c0.3,0,0.6-0.1,0.9-0.3l14.2-13.2c0.2-0.2,0.4-0.5,0.4-0.8s-0.1-0.6-0.3-0.9l-7.1-7.7c-0.1-0.1-0.2-0.2-0.4-0.3
        C15.8,1,15.6,1,15.4,1s-0.3,0-0.5,0.1C14.8,1.1,14.7,1.2,14.6,1.3z"
        />
        <path
          className={logoClass}
          d="M28.3,31c0.6,0.2,1.1-0.2,0.9-0.8l-4.1-17.8c-0.2-0.6-0.7-0.8-1.2-0.4L10.5,24.5c-0.5,0.5-0.4,1,0.3,1.2
        L28.3,31z"
        />
        <path
          className={logoClass}
          d="M35.3,6.2l-16.2-5c-0.6-0.2-0.8,0.1-0.2,0.6l5.9,6.4c0.3,0.2,0.6,0.4,0.9,0.5c0.4,0.1,0.7,0.1,1.1,0l8.5-1.9
        C36,6.7,36,6.4,35.3,6.2z"
        />
        <path
          className={logoClass}
          d="M265.3,13.9c-0.9,0-1.7-0.2-2.4-0.6c-0.7-0.4-1.3-1-1.7-1.7c-0.4-0.7-0.6-1.5-0.6-2.4c0-0.9,0.2-1.7,0.6-2.4
        s1-1.3,1.7-1.7c0.7-0.4,1.5-0.6,2.4-0.6s1.7,0.2,2.4,0.6c0.7,0.4,1.3,1,1.7,1.7c0.4,0.7,0.6,1.5,0.6,2.4c0,0.9-0.2,1.7-0.6,2.4
        s-1,1.3-1.7,1.7C267,13.7,266.2,13.9,265.3,13.9z M265.3,13c0.7,0,1.4-0.2,1.9-0.5c0.6-0.3,1-0.8,1.4-1.4c0.3-0.6,0.5-1.3,0.5-2
        s-0.2-1.4-0.5-2s-0.8-1.1-1.4-1.4s-1.2-0.5-1.9-0.5c-0.7,0-1.4,0.2-1.9,0.5c-0.6,0.3-1,0.8-1.4,1.4c-0.3,0.6-0.5,1.2-0.5,2
        s0.2,1.4,0.5,2s0.8,1,1.4,1.4C263.9,12.9,264.6,13,265.3,13z M263.7,11.3V6.8h1.5c0.6,0,1,0.1,1.3,0.4s0.5,0.6,0.5,1.1
        c0,0.3-0.1,0.5-0.2,0.8c-0.1,0.2-0.3,0.4-0.6,0.5l1.1,1.8h-0.9l-1-1.6h-0.9v1.6h-0.8V11.3z M265.1,7.5h-0.6V9h0.6
        c0.4,0,0.6-0.1,0.8-0.2c0.1-0.1,0.2-0.3,0.2-0.6C266.1,7.7,265.8,7.5,265.1,7.5z"
        />
      </svg>
    )
  }
)

InfluxDataLogo.displayName = 'InfluxDataLogo'
